/* @flow */
import _ from 'lodash';

import type {
  ReduxState,
  Action,
  Fetchable,
  TrackSearchFullParams,
  TrackSearchParams,
  TrackAggregationKey,
  TrackSearchResultList,
  Track,
  Aggregations,
} from '../../types';
import { trackSearchParamsProps } from '../../types/TrackSearchParams';
import { getSearchParams, getSubmittedSearchParams } from '../../redux/reducers/app';

// ===========================
//   STATE TYPE
// ===========================

export type TrackSearchState = {
  searchParams?: TrackSearchParams,
  submittedSearchParams?: TrackSearchFullParams,
  results: Fetchable<TrackSearchResultList>,
  selectedIndexes: number[],
  pageScroll: number,
  selectedTracks: {
    [trackId: number]: boolean,
  },
  expandedTracks: {
    [trackId: number]: boolean,
  },
};

// ===========================
//   INITIAL STATE
// ===========================

const initialPageScroll = 0;
const initialSelectedTracks = {};
const initialExpandedTracks = {};

export const initialState: TrackSearchState = {
  results: { fetching: false },
  selectedIndexes: [],
  pageScroll: initialPageScroll,
  selectedTracks: initialSelectedTracks,
  expandedTracks: initialExpandedTracks,
};

const isStaleRequest = (params?: TrackSearchParams, requestParams: TrackSearchParams) => {
  return !_.isEqual(params, requestParams);
};

// ===========================
//   REDUCER
// ===========================

export default (state: TrackSearchState = initialState, action: Action): TrackSearchState => {
  switch (action.type) {
    case 'SET_TRACK_SEARCH': {
      const fullSearchParams: TrackSearchFullParams = action.payload;
      const searchParams: TrackSearchParams = _.pick(fullSearchParams, trackSearchParamsProps);
      return { ...state, searchParams };
    }
    case 'PERFORM_TRACK_SEARCH_REQUEST': {
      const fullSearchParams: TrackSearchFullParams = action.payload;
      const searchParams: TrackSearchParams = _.pick(fullSearchParams, trackSearchParamsProps);
      const data = { ...state.results.data };
      delete data.hits;
      return {
        ...state,
        searchParams,
        submittedSearchParams: fullSearchParams,
        results: { fetching: true, data },
      };
    }
    case 'PERFORM_TRACK_SEARCH_SUCCESS': {
      if (!action.meta.initialPayload.text) {
        return {
          ...state,
          results: { fetching: false },
          selectedIndexes: [],
          pageScroll: initialPageScroll,
          selectedTracks: initialSelectedTracks,
          expandedTracks: initialExpandedTracks,
        };
      }
      if (!isStaleRequest(state.submittedSearchParams, action.meta.initialPayload)) {
        const data = action.payload;
        return {
          ...state,
          results: { fetching: false, data },
          selectedIndexes: [],
          pageScroll: initialPageScroll,
          selectedTracks: initialSelectedTracks,
          expandedTracks: initialExpandedTracks,
        };
      } else {
        // ignore results
        return state;
      }
    }
    case 'PERFORM_TRACK_SEARCH_FAILURE': {
      if (!isStaleRequest(state.submittedSearchParams, action.meta.initialPayload)) {
        const error = action.payload;
        return {
          ...state,
          results: { fetching: false, error },
          selectedIndexes: [],
          pageScroll: initialPageScroll,
          selectedTracks: initialSelectedTracks,
          expandedTracks: initialExpandedTracks,
        };
      } else {
        // ignore results
        return state;
      }
    }

    case 'FETCH_TRACK_SEARCH_PAGE_REQUEST': {
      return {
        ...state,
        results: {
          ...state.results,
          fetching: true,
        },
      };
    }
    case 'FETCH_TRACK_SEARCH_PAGE_SUCCESS': {
      const previousData = state.results.data || {};
      return {
        ...state,
        results: {
          fetching: false,
          data: {
            ...previousData,
            hits: (previousData.hits || []).concat(action.payload.hits),
          },
        },
        selectedIndexes: [],
        pageScroll: initialPageScroll,
        selectedTracks: initialSelectedTracks,
        expandedTracks: initialExpandedTracks,
      };
    }
    case 'FETCH_TRACK_SEARCH_PAGE_FAILURE': {
      return {
        ...state,
        results: { fetching: false, error: action.payload },
        selectedIndexes: [],
        pageScroll: initialPageScroll,
        selectedTracks: initialSelectedTracks,
        expandedTracks: initialExpandedTracks,
      };
    }
    case 'TOGGLE_SELECT_TRACK': {
      const {
        selectedIndexes,
        results: { data },
      } = state;
      return {
        ...state,
        selectedIndexes: data
          ? _.includes(selectedIndexes, action.payload)
            ? _.without(selectedIndexes, action.payload)
            : [...selectedIndexes, action.payload]
          : [],
      };
    }
    case 'TOGGLE_SELECT_ALL_TRACKS': {
      return {
        ...state,
        selectedIndexes:
          state.results.data &&
          state.results.data.hits &&
          state.selectedIndexes.length !== state.results.data.hits.length
            ? _.range(state.results.data.hits.length)
            : [],
      };
    }
    case 'TRACK_SEARCH_SCROLL_PAGE': {
      const pageScroll = action.payload || 0;
      return {
        ...state,
        pageScroll,
      };
    }
    case 'TRACK_SEARCH_EXPAND_TRACK': {
      const { trackId, isExpanded } = action.payload;
      const { expandedTracks } = state;
      expandedTracks[trackId] = isExpanded;
      return {
        ...state,
        expandedTracks,
      };
    }
    case 'TRACK_SEARCH_TOGGLE_SELECT_TRACKS': {
      const trackIds = action.payload;
      const { selectedTracks } = state;
      const selectedTrackIds = _.map(_.keys(_.pickBy(selectedTracks, v => v === true)), v =>
        Number(v)
      );
      const isAllSelected = trackIds.every(id => selectedTrackIds.includes(id));
      for (const trackId of trackIds) {
        selectedTracks[trackId] = !isAllSelected;
      }
      return {
        ...state,
        selectedTracks,
      };
    }
    case 'TRACK_SEARCH_TOGGLE_EXPAND_SELECTED_TRACKS': {
      const { expandedTracks, selectedTracks } = state;
      const selectedTrackIds = _.map(_.keys(_.pickBy(selectedTracks, v => v === true)), v =>
        Number(v)
      );
      const expandedTrackIds = _.map(_.keys(_.pickBy(expandedTracks, v => v === true)), v =>
        Number(v)
      );
      const isAllExpanded = selectedTrackIds.every(id => expandedTrackIds.includes(id));
      for (const trackId of selectedTrackIds) {
        expandedTracks[trackId] = !isAllExpanded;
      }
      return {
        ...state,
        expandedTracks,
      };
    }

    case 'LOGOUT_SUCCESS':
      return initialState;

    default: {
      return state;
    }
  }
};

// ===========================
//   SELECTORS
// ===========================

export const getCategoryIsSelected = (state: ReduxState): boolean[] => {
  const temp: boolean[] = [];

  temp.push(!!state.news?.searchParams?.categoryIds?.length);
  temp.push(!!state.trackSearch?.searchParams?.filters?.categories?.length);
  temp.push(!!state.mediaSearch?.searchParams?.categoryIds?.length);

  return temp;
};

export const getTrackSearchLength = (state: ReduxState): number => {
  if (!state.trackSearch.results?.data) return 0;
  return state.trackSearch.results.data.total;
};

export const getTrackSearchParams = (state: ReduxState): TrackSearchFullParams => {
  return _.merge({}, getSearchParams(state), state.trackSearch.searchParams);
};

export const getSubmittedTrackSearchParams = (state: ReduxState): TrackSearchFullParams => {
  return _.merge({}, getSubmittedSearchParams(state), state.trackSearch.submittedSearchParams);
};

export const getTrackList = (state: ReduxState): Fetchable<Track[]> => {
  const { fetching, error, data } = state.trackSearch.results;
  return {
    fetching,
    error,
    data: data && data.hits,
  };
};

export const getAggregations = (
  state: ReduxState
): Fetchable<Aggregations<TrackAggregationKey>> => {
  const { fetching, error, data } = state.trackSearch.results;
  return {
    fetching,
    error,
    data: data && data.aggregations,
  };
};

export const getTotal = (state: ReduxState): Fetchable<number> => {
  const { fetching, error, data } = state.trackSearch.results;
  return {
    fetching,
    error,
    data: data && data.total,
  };
};

export const isSelectedAll = (state: ReduxState): boolean => {
  const {
    selectedIndexes,
    results: { data },
  } = state.trackSearch;
  return data && data.hits && data.hits.length !== 0
    ? data.hits.length === selectedIndexes.length
    : false;
};

export const getHasMoreResults = (state: ReduxState): boolean => {
  const {
    results: { data },
  } = state.trackSearch;
  return data !== undefined && data.hits !== undefined && data.hits.length < data.total;
};

export const getSelectedTrackIds = (state: ReduxState): number[] => {
  return _.map(_.keys(_.pickBy(state.trackSearch.selectedTracks, v => v === true)), v => Number(v));
};

export const getExpandedTrackIds = (state: ReduxState): number[] => {
  return _.map(_.keys(_.pickBy(state.trackSearch.expandedTracks, v => v === true)), v => Number(v));
};
